import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/efs/PageHeader';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { PurgeMasterDialog } from '@/components/efs/PurgeMasterDialog';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterExportButton } from '@/components/efs/MasterExportButton';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { DismantlingMethodsFormDialog } from './DismantlingMethodsFormDialog';
import {
  useActivateDismantlingMethod,
  useDeactivateDismantlingMethod,
  useDismantlingMethodsList,
  usePurgeDismantlingMethod,
} from './useDismantlingMethods';
import type { DismantlingMethodMaster } from './types';

const NS = 'masters';

const RowActions = ({ row }: { row: DismantlingMethodMaster }) => {
  const { t } = useTranslation();
  const deactivate = useDeactivateDismantlingMethod();
  const activate = useActivateDismantlingMethod();
  const purge = usePurgeDismantlingMethod();
  const [editOpen, setEditOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('efs.masters.actions.row_menu', { ns: NS })}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setEditOpen(true)}>
            {t('efs.masters.actions.edit', { ns: NS })}
          </DropdownMenuItem>
          {row.active_flag ? (
            <DropdownMenuItem
              onSelect={() => deactivate.mutate(row.dismantling_method_id)}
              disabled={deactivate.isPending}
            >
              {t('efs.masters.dismantling_methods.actions.deactivate', { ns: NS })}
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem
                onSelect={() => activate.mutate(row.dismantling_method_id)}
                disabled={activate.isPending}
              >
                {t('efs.masters.dismantling_methods.actions.activate', { ns: NS })}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  purge.reset();
                  setPurgeOpen(true);
                }}
                disabled={row.in_use}
                className="text-red-900 focus:text-red-900"
              >
                {t('efs.masters.actions.purge', { ns: NS })}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <PurgeMasterDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        name={row.display_name}
        isPending={purge.isPending}
        error={purge.error}
        onConfirm={() =>
          purge.mutate(row.dismantling_method_id, { onSuccess: () => setPurgeOpen(false) })
        }
      />

      <DismantlingMethodsFormDialog open={editOpen} onOpenChange={setEditOpen} row={row} />
    </>
  );
};

/**
 * The processor's own dismantling methods.
 *
 * The platform used to define exactly two — 手動軽分解 and 手動重分解 — and put
 * that choice into routing, so a site that only dismantles one way was asked a
 * question with no answer. This list is the site's own, and routing now says
 * only that something is dismantled by hand.
 */
export const DismantlingMethodsPage = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [createOpen, setCreateOpen] = useState(false);

  const query = useDismantlingMethodsList();
  const rows = useMemo(() => query.data ?? [], [query.data]);
  const canManage = hasPermission(user, 'master.approve');

  const columns = useMemo<MasterTableColumn<DismantlingMethodMaster>[]>(
    () => [
      {
        key: 'method_code',
        header: t('efs.masters.dismantling_methods.fields.method_code', { ns: NS }),
        cell: (row) => <span className="font-mono text-xs">{row.method_code}</span>,
      },
      {
        key: 'display_name',
        header: t('efs.masters.dismantling_methods.fields.display_name', { ns: NS }),
        cell: (row) => row.display_name,
      },
      {
        key: 'description',
        header: t('efs.masters.dismantling_methods.fields.description', { ns: NS }),
        cell: (row) => (
          <span className="text-xs text-efs-text-muted">{row.description ?? '—'}</span>
        ),
      },
      {
        key: 'in_use',
        header: t('efs.masters.dismantling_methods.fields.in_use', { ns: NS }),
        align: 'center',
        cell: (row) =>
          row.in_use ? (
            <Badge variant="secondary">
              {t('efs.masters.dismantling_methods.in_use_yes', { ns: NS })}
            </Badge>
          ) : (
            <span className="text-xs text-efs-text-muted">—</span>
          ),
      },
      {
        key: 'active',
        header: t('efs.masters.package_types.fields.active', { ns: NS }),
        cell: (row) =>
          row.active_flag ? (
            <Badge variant="default">
              {t('efs.common.status.approved', { defaultValue: '有効' })}
            </Badge>
          ) : (
            <Badge variant="outline">
              {t('efs.common.status.retired', { defaultValue: '無効' })}
            </Badge>
          ),
      },
      ...(canManage
        ? [
            {
              key: 'actions',
              header: '',
              align: 'right' as const,
              cell: (row: DismantlingMethodMaster) => <RowActions row={row} />,
            },
          ]
        : []),
    ],
    [canManage, t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.masters.dismantling_methods.title', { ns: NS })}
        subtitle={t('efs.masters.dismantling_methods.subtitle', { ns: NS })}
        count={rows.length}
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('efs.masters.actions.create', { ns: NS })}
            </Button>
          ) : null
        }
      />

      <TableToolbar
        count={t('efs.masters.toolbar.count', { ns: NS, count: rows.length })}
        actions={<MasterExportButton kind="dismantling-methods" />}
      />

      {!query.isLoading && rows.length === 0 ? (
        <div className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-8 text-center text-sm text-efs-text-muted">
          {t('efs.masters.dismantling_methods.empty', { ns: NS })}
        </div>
      ) : (
        <MasterTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.dismantling_method_id}
          isLoading={query.isLoading}
        />
      )}

      <DismantlingMethodsFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default DismantlingMethodsPage;
